import type * as React from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import CopyToClipboard from '@cloudscape-design/components/copy-to-clipboard';
import FormField from '@cloudscape-design/components/form-field';
import Modal from '@cloudscape-design/components/modal';
import SpaceBetween from '@cloudscape-design/components/space-between';
import type { CreateApiKeyResult } from '@etiquetachef/types';

type Props = {
  credentials: CreateApiKeyResult | null;
  onDismiss: () => void;
};

/**
 * Entrega das credenciais recém-criadas (issue #263).
 *
 * Origem: #263 — commit 89ca9fd6e, 2026-08-24 15:39 +0200.
 *
 * Por quê: esta tela é a ÚNICA vez que o `client_secret` existe: o backend
 * guarda só o sha256, então fechar sem copiar significa revogar a integração e
 * criar outra. Por isso o aviso é `type="warning"` e o botão de fechar diz
 * explicitamente que a credencial foi guardada — não um "OK" neutro.
 *
 * Contrato: renderiza `clientId` e `clientSecret` como texto copiável
 * (Cloudscape `CopyToClipboard` inline) dentro de um bloco `ph-no-capture`.
 * Sem efeito colateral além da área de transferência.
 *
 * Usado por: `routes/ApiKeysPage.tsx` (depois de `adminCreateApiKey`). Spec:
 * `docs/screens/api-keys-admin.md`.
 *
 * Falhas conhecidas:
 *  - 2026-10-03 (#795, OBS-2): o segredo era texto cru na tela de um app com
 *    session replay ligado sem máscara de texto — ia para o PostHog. O replay
 *    do admin foi desligado (`lib/posthog.ts`); o `ph-no-capture` aqui é a
 *    defesa em profundidade, para não depender daquela configuração.
 *
 * Dicas: `ph-no-capture` é a classe que o posthog-js respeita nas duas pontas —
 * o autocapture ignora cliques dentro dela e o rrweb (replay) bloqueia o bloco
 * inteiro. Teste: `ApiKeyCreatedModal.test.tsx`.
 *
 * Histórico:
 *  - 2026-08-24 15:39 +0200 — (#263) criado.
 *  - 2026-10-03 14:34 +0200 — (#795, OBS-2) credenciais sob `ph-no-capture`.
 */
export function ApiKeyCreatedModal({ credentials, onDismiss }: Props): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <Modal
      visible={credentials !== null}
      // Sem `onDismiss` no X/ESC: fechar por engano custa a credencial.
      // A saída é o botão do rodapé, que afirma que ela foi guardada.
      onDismiss={(): void => undefined}
      header={t('admin.apiKeys.created.title')}
      footer={
        <Box float="right">
          <Button variant="primary" onClick={onDismiss}>
            {t('admin.apiKeys.created.done')}
          </Button>
        </Box>
      }
    >
      {/* Bug corrigido em 2026-10-03 14:34 +0200 (#795, OBS-2): o
          `client_secret` aparece como TEXTO e o replay do admin gravava texto
          cru. `ph-no-capture` tira o bloco do replay e do autocapture mesmo se
          alguém religar o replay. Não remover. A regra de `className` do admin
          proíbe classe de ESTILO (Tailwind); esta é o marcador do posthog-js,
          que não tem prop equivalente no Cloudscape nem forma por atributo. */}
      {/* eslint-disable-next-line no-restricted-syntax */}
      <div className="ph-no-capture">
        <SpaceBetween size="l">
          <Alert type="warning">{t('admin.apiKeys.created.warning')}</Alert>

          <FormField
            label={t('admin.apiKeys.fields.clientId')}
            description={t('admin.apiKeys.created.clientIdHint')}
          >
            <CopyToClipboard
              variant="inline"
              textToCopy={credentials?.clientId ?? ''}
              copyButtonAriaLabel={t('admin.apiKeys.created.copyClientId')}
              copySuccessText={t('admin.apiKeys.created.copied')}
              copyErrorText={t('admin.apiKeys.created.copyFailed')}
            />
          </FormField>

          <FormField
            label={t('admin.apiKeys.fields.clientSecret')}
            description={t('admin.apiKeys.created.clientSecretHint')}
          >
            <CopyToClipboard
              variant="inline"
              textToCopy={credentials?.clientSecret ?? ''}
              copyButtonAriaLabel={t('admin.apiKeys.created.copyClientSecret')}
              copySuccessText={t('admin.apiKeys.created.copied')}
              copyErrorText={t('admin.apiKeys.created.copyFailed')}
            />
          </FormField>
        </SpaceBetween>
      </div>
    </Modal>
  );
}
